/**
 * apiError.js — Consistent error message extraction across the app.
 * ────────────────────────────────────────────────────────────────
 * Historically we had `err.response?.data?.detail || "An error occurred"`
 * scattered everywhere. When FastAPI 422s, `detail` is an ARRAY of
 * objects — assigning that to React state and rendering it triggers
 * "Objects are not valid as a React child" (#31), which is exactly
 * what crashed Special Edition in iter 37. And when TLS/DNS drops the
 * request entirely (`ylcenter.com` handshake failure), `err.response`
 * is undefined and the user just sees the generic string.
 *
 * ADDITIONAL SAFEGUARD (2026-02-21): when the origin is overloaded and
 * Cloudflare intercepts with a 5xx HTML error page, `err.response.data`
 * is the raw HTML ("The origin web server sent a response that
 * Cloudflare could not parse..."). If a caller renders that directly,
 * the student sees a giant, terrifying, technical block instead of a
 * friendly message. `extractApiError` now detects any HTML-shaped
 * string and swaps it for the friendly 5xx fallback.
 *
 * `extractApiError(err, fallback)` returns a readable string suitable
 * for direct render in a UI toast / banner. Distinguishes:
 *   - Network / TLS / DNS failure    → "Can't reach the server…"
 *   - Timeout (ECONNABORTED)         → "Request timed out…"
 *   - 5xx / Cloudflare HTML page     → "Server is busy, please try shortly"
 *   - 4xx with string detail         → the string, as-is
 *   - 4xx with array of Pydantic errors → "; "-joined msgs
 *   - Everything else                → the caller-provided fallback
 */

// A string that *looks* like an HTML document / Cloudflare error page.
// Anything starting with `<` (after trim), or explicitly mentioning
// Cloudflare's edge-error phrasing, must never leak to the UI.
function _looksLikeHtml(s) {
  if (typeof s !== "string") return false;
  const t = s.trimStart();
  if (t.startsWith("<")) return true;
  const lower = t.toLowerCase();
  return (
    lower.includes("cloudflare") ||
    lower.includes("<!doctype") ||
    lower.includes("origin web server")
  );
}

export function extractApiError(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return fallback;

  // Client-side timeout / abort
  if (err.code === "ECONNABORTED" || String(err.message || "").toLowerCase().includes("timeout")) {
    return "The request timed out. Please check your connection and try again.";
  }

  // No response object at all → network / TLS / CORS / DNS failure
  if (!err.response) {
    return "We can't reach the server right now. Please check your internet connection or try again in a moment.";
  }

  const status = err.response.status;
  let raw = err.response.data?.detail ?? err.response.data?.message;

  // Cloudflare / origin overload leaks the full HTML page into
  // `response.data` (a string, no wrapper object). Detect + suppress.
  if (raw === undefined && typeof err.response.data === "string") {
    raw = err.response.data;
  }
  if (_looksLikeHtml(raw)) {
    raw = null;
  }

  if (typeof raw === "string" && raw.trim()) return raw;

  if (Array.isArray(raw)) {
    const joined = raw
      .map((d) => (d && typeof d === "object" ? (d.msg || JSON.stringify(d)) : String(d)))
      .filter(Boolean)
      .join("; ");
    if (joined && !_looksLikeHtml(joined)) return joined;
  }

  if (status >= 500) return "That took longer than expected. Please try again.";
  if (status === 429) return "Too many requests. Please wait a moment and try again.";
  if (status === 404) return "That resource wasn't found.";
  return fallback;
}

/**
 * Belt-and-suspenders: an axios *response* interceptor that scrubs
 * HTML-shaped `error.response.data` at the source, so even callers that
 * bypass `extractApiError` and read `err.response.data.detail` directly
 * can never render a Cloudflare error page into the UI.
 *
 * Call once at app boot (App.js does this).
 */
export function installHtmlErrorScrubber(axiosInstance) {
  return axiosInstance.interceptors.response.use(
    (r) => r,
    (error) => {
      try {
        const d = error?.response?.data;
        if (_looksLikeHtml(d)) {
          error.response.data = {
            detail: "That took longer than expected. Please try again.",
            _origin_html_suppressed: true,
          };
        } else if (d && typeof d === "object" && _looksLikeHtml(d.detail)) {
          d.detail = "That took longer than expected. Please try again.";
        }
      } catch { /* never let the interceptor itself throw */ }
      return Promise.reject(error);
    }
  );
}

/**
 * Transient-failure retry: brief 502/503/504 or dropped connections (e.g. a
 * backend restart or a moment of heavy load) are retried quietly so students
 * don't see an error for a blip. Only idempotent GET/HEAD requests are retried.
 */
const RETRY_DELAYS_MS = [800, 2000, 4000];

export function installTransientRetry(axiosInstance) {
  return axiosInstance.interceptors.response.use(
    (r) => r,
    async (error) => {
      const cfg = error?.config;
      const method = (cfg?.method || "get").toLowerCase();
      const status = error?.response?.status;
      const transient = !error?.response
        ? error?.code !== "ECONNABORTED" && error?.code !== "ERR_CANCELED"
        : [502, 503, 504].includes(status);
      if (!cfg || !transient || !["get", "head"].includes(method)) {
        return Promise.reject(error);
      }
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      if (cfg.__retryCount > RETRY_DELAYS_MS.length) return Promise.reject(error);
      await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[cfg.__retryCount - 1]));
      return axiosInstance(cfg);
    }
  );
}
